import React, { useEffect, useState, useTransition } from 'react';
import {
    Container,
    Box,
    SxProps,
    Theme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Rating,
    Stack,
} from '@mui/material';
import { ITalentReview, IWorkProfile } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import WorkProfileHeader from './WorkProfileHeader';
import { CustomSegmentedControl } from '../SegmentedControl/CustomSegmentedControl';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { CastingProfile, TUpdateBody } from './CastingProfile';
import { Media } from './Media';
import { Reviews } from './Reviews';
import { WorkProfileSelector } from './WorkProfileSelector';
import { useDispatch } from 'react-redux';
import {
    createTalentReview,
    // getTalentReviews,
    updateTalentProfile,
    updateTalentReview,
    deleteTalentReview,
    likeTalentReview,
    dislikeTalentReview,
} from '../../redux/talentProfile/action';
import { deleteLike } from '../../redux/project/actions';
import CustomButton from '../Button/CustomButton';
import Favorite from '@mui/icons-material/Favorite';
import Close from '@mui/icons-material/Close';
import Mail from '@mui/icons-material/Mail';
import { IApplication } from '../../types/global/ListRender.types';
import { ICreateTalentReview } from '../../redux/talentProfile/types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { catchAsync, getIdForIbjects, useDispatchPromise } from '../../utils/AppUtils';
import { toggleAuthModal } from '../../redux/authentication/action';
import { useAuth } from '../../module/AuthContext';
import { CreditsTimeline } from './CreditsTimeline';
import { MessageDialogue } from '../MessageDialogue/MessageDialogue';

const CHAR_LIMIT = 300;
const SUBJECT_LIMIT = 50;

export interface IWorkProfileProps {
    workProfile?: IWorkProfile[];
    isSummary?: boolean;
    reviews?: ITalentReview[];
    external?: boolean;
    isEditMode?: boolean;
    /** Determines when this is being viewed by a casting agency or poster */
    isPreviewMode?: boolean;
    applicationData?: IApplication;
    onAcceptApplication?: () => void;
    onRejectApplication?: () => void;
    onMessageApplication?: () => void;
    searchFlow?: boolean;
    isPublicMode?: boolean;
    userName?: string;
}

const SUMMARY_TAB = ['Details & Media', 'Reviews', 'Credits'];
const DETAILS_TAB = ['Details', 'Media', 'Reviews', 'Credits'];

// Add this new component for the preview mode action buttons
interface PreviewModeActionsProps {
    onAcceptClick?: () => void;
    onRejectClick?: () => void;
    onMessageClick?: () => void;
    mainSx?: SxProps<Theme>;
    searchFlow?: boolean;
    isPublicMode?: boolean;
}

const PreviewModeActions: React.FC<PreviewModeActionsProps> = ({
    onAcceptClick,
    onRejectClick,
    onMessageClick,
    mainSx,
    searchFlow,
    isPublicMode,
}) => {
    const { themeStyle } = useThemeContext();
    const { isSignedIn } = useAuth();
    const dispatch = useDispatch();

    if (!isSignedIn) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                m={2}
                sx={{
                    position: 'relative',
                    backgroundColor: theme[themeStyle].form_gray,
                    padding: '15px',
                    borderRadius: 4,
                    border: `1px solid ${theme[themeStyle].tag_white}`,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].tag_white,
                        fontSize: {
                            xs: 8,
                            sm: 12,
                        },
                    }}>
                    Sign In to contact talent.
                </Typography>
                <CustomButton
                    label="Sign In"
                    onClick={() => dispatch(toggleAuthModal(true))}
                    sx={{
                        mx: 1,
                        '&:hover': {
                            WebkitTextFillColor: theme[themeStyle].primary_orange,
                        },
                    }}
                    textStyle={{ fontSize: 12 }}
                    customStyle={{ height: 20 }}
                />
            </Stack>
        );
    }

    if (isPublicMode) {
        return (
            <Box
                display="flex"
                alignItems="center"
                gap={2}
                width={'100%'}
                justifyContent={'flex-start'}
                p={2}
                sx={mainSx}>
                <CustomButton
                    label="Message"
                    withIcon={true}
                    beginningIcon={<Mail />}
                    onClick={onMessageClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].main_blue,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].primary,
                        color: theme[themeStyle].primary,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary,
                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={2} width={'100%'} justifyContent={'flex-end'} p={2} sx={mainSx}>
            {!searchFlow && (
                <CustomButton
                    label="Reject"
                    withIcon={true}
                    beginningIcon={<Close />}
                    onClick={onRejectClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].secondary_red,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].primary_red,
                        color: theme[themeStyle].secondary_red,
                        '&:hover': {
                            borderColor: theme[themeStyle].primary_red,
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        },
                    }}
                />
            )}
            {!searchFlow && (
                <CustomButton
                    label="Accept"
                    withIcon={true}
                    beginningIcon={<Favorite />}
                    onClick={onAcceptClick}
                    buttonProps={{ variant: 'outlined' }}
                    textStyle={{
                        color: theme[themeStyle].switchGreen,
                    }}
                    sx={{
                        borderColor: theme[themeStyle].tertiary_green,
                        color: theme[themeStyle].tertiary_green,
                        '&:hover': {
                            borderColor: theme[themeStyle].tertiary_green,
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        },
                    }}
                />
            )}
            <CustomButton
                label="Message"
                withIcon={true}
                beginningIcon={<Mail />}
                onClick={onMessageClick}
                buttonProps={{ variant: 'outlined' }}
                textStyle={{
                    color: theme[themeStyle].main_blue,
                }}
                sx={{
                    borderColor: theme[themeStyle].primary,
                    color: theme[themeStyle].primary,
                    '&:hover': {
                        borderColor: theme[themeStyle].primary,
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    },
                }}
            />
        </Box>
    );
};

const ReviewModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onSubmit: (data: ICreateTalentReview) => void;
    talentId: string;
    initialRating?: number;
    initialSubject?: string;
    initialText?: string;
}> = ({ open, onClose, onSubmit, talentId, initialRating, initialSubject, initialText }) => {
    const [rating, setRating] = useState<number | null>(initialRating ?? 2.5);
    const [reviewSubject, setReviewSubject] = useState(initialSubject ?? '');
    const [reviewText, setReviewText] = useState(initialText ?? '');
    const { themeStyle } = useThemeContext();

    // Reset modal state when opened or when editing different review
    React.useEffect(() => {
        if (open) {
            setRating(initialRating ?? 2.5);
            setReviewSubject(initialSubject ?? '');
            setReviewText(initialText ?? '');
        }
    }, [open, initialRating, initialSubject, initialText]);

    const handleSubmit = () => {
        if (talentId) {
            onSubmit({
                rating: rating ?? 2.5,
                review_subject: reviewSubject,
                review_text: reviewText,
                talent: talentId,
            });
            setRating(2.5);
            setReviewSubject('');
            setReviewText('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ bgcolor: theme[themeStyle].secondaryBg, color: theme[themeStyle].tag_white }}>
                Add a Review
            </DialogTitle>
            <DialogContent sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                <Box sx={{ mt: 2 }}>
                    <Typography
                        gutterBottom
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].tag_white }}>
                        Rating
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Rating
                            value={rating}
                            onChange={(_, newValue) => setRating(newValue)}
                            precision={0.1}
                            max={5}
                            sx={{
                                '& .MuiRating-iconFilled': {
                                    color: theme[themeStyle].highlight,
                                },
                                '& .MuiRating-iconHover': {
                                    color: theme[themeStyle].highlight,
                                },
                                '& .MuiRating-iconEmpty': {
                                    color: theme[themeStyle].highlight, // <- outline color for empty stars
                                },
                            }}
                        />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].highlight }}>
                            {rating?.toFixed(1) ?? '0.0'}
                        </Typography>
                    </Box>
                    <TextField
                        label="Subject"
                        value={reviewSubject}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.length <= SUBJECT_LIMIT) {
                                setReviewSubject(e.target.value);
                            }
                        }}
                        fullWidth
                        InputLabelProps={{
                            sx: {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.4,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                        InputProps={{
                            sx: {
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                backgroundColor: theme[themeStyle].gray_text,
                            },
                            '& .MuiSelect-select': {
                                backgroundColor: theme[themeStyle].gray_text,
                            },
                        }}
                    />
                    <TextField
                        label="Review"
                        value={reviewText}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.length <= CHAR_LIMIT) {
                                setReviewText(value);
                            }
                        }}
                        fullWidth
                        multiline
                        minRows={3}
                        InputLabelProps={{
                            sx: {
                                color: theme[themeStyle].tag_white,
                                opacity: 0.4,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                        InputProps={{
                            sx: {
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: theme[themeStyle].tag_white,
                                backgroundColor: theme[themeStyle].gray_text,
                            },
                            '& .MuiSelect-select': {
                                backgroundColor: theme[themeStyle].gray_text,
                            },
                            mb: 1,
                        }}
                    />
                    <Typography sx={{ textAlign: 'right', color: theme[themeStyle].tag_white, opacity: 0.5 }}>
                        {reviewText.length}/{CHAR_LIMIT}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 4, bgcolor: theme[themeStyle].secondaryBg, color: theme[themeStyle].tag_white }}>
                <CustomButton buttonProps={{ variant: 'text' }} label="Cancel" onClick={onClose} />
                <CustomButton
                    disabled={!rating || !reviewSubject || !reviewText}
                    label="Submit"
                    onClick={handleSubmit}
                    customStyle={{
                        borderRadius: '4px',
                    }}
                />
            </DialogActions>
        </Dialog>
    );
};

const WorkProfileWrapper: React.FC<IWorkProfileProps> = ({
    workProfile,
    isSummary,
    reviews,
    external,
    isEditMode,
    isPreviewMode,
    applicationData,
    onAcceptApplication,
    onRejectApplication,
    onMessageApplication,
    searchFlow,
    isPublicMode,
    userName,
}) => {
    const [, startTransition] = useTransition();
    const [slideName, setSlideName] = React.useState<string>(isSummary ? SUMMARY_TAB[0] : DETAILS_TAB[0]);
    const { themeStyle } = useThemeContext();
    const [selectedWorkProfile, setSelectedWorkProfile] = useState(workProfile && workProfile[0]);
    const [editMode, setEditMode] = useState(false);
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [editingReview, setEditingReview] = useState<ITalentReview | null>(null);
    const [deleteReview, setDeleteReview] = useState<ITalentReview | null>(null);
    const [showMessage, setShowMessage] = useState(false);

    const handleUpdate = () => {
        handleProfileUpdate();
    };

    useEffect(() => {
        if (isEditMode) setEditMode(isEditMode);
    }, [isEditMode]);

    const handleProfileUpdate = catchAsync(async (payload: TUpdateBody) => {
        if (workProfile && selectedWorkProfile) {
            const data: IWorkProfile = (await dispatchPromise(
                updateTalentProfile({
                    id: selectedWorkProfile?._id,
                    body: payload.body,
                    uploads: payload.uploads,
                    fromCreate: false,
                    /**
                     * This value here is the same cache that is stored in the backend.
                     * To understand this, trace the api call and see the cache value.
                     */
                    cache: `${userName}-profile`,
                }),
            )) as IWorkProfile;
            setEditMode(false);
            setSelectedWorkProfile(data);
        }
    });

    const handleLeaveReview = (data: ICreateTalentReview) => {
        dispatch(
            createTalentReview({
                ...data,
                /**
                 * This value here is the same cache that is stored in the backend.
                 * To understand this, trace the api call and see the cache value.
                 */
                cache: [`${userName}-profile`, `TALENT_REVIEWS_${data.talent}`],
            }),
        );
    };

    const handleOnProfileSelect = (profile: IWorkProfile) => {
        if (profile && profile._id) {
            // Removed as was triggering a second api call
            // dispatch(getTalentReviews({ talentId: profile._id, limit: 10, page: 1 }));
            setSelectedWorkProfile(profile);
        }
    };

    const handleEditReview = (review: ITalentReview) => {
        setEditingReview(review);
        setReviewModalOpen(true);
    };
    const handleDeleteReview = (review: ITalentReview) => {
        setDeleteReview(review);
    };
    const handleConfirmDelete = () => {
        if (deleteReview && deleteReview._id) {
            dispatch(deleteTalentReview(deleteReview._id));
            setDeleteReview(null);
        }
    };
    const handleCancelDelete = () => {
        setDeleteReview(null);
    };
    const handleEditReviewSubmit = (data: ICreateTalentReview) => {
        if (editingReview) {
            dispatch(
                updateTalentReview({
                    id: editingReview._id,
                    review_subject: data.review_subject,
                    review_text: data.review_text,
                    rating: data.rating,
                    cache: [`${userName}-profile`, `TALENT_REVIEWS_${data.talent}`],
                }),
            );
            setEditingReview(null);
            setReviewModalOpen(false);
        }
    };
    const handleLikeReview = (review: ITalentReview) => {
        dispatch(
            likeTalentReview({
                reviewId: review._id,
                type: 'like',
                cache: [`TALENT_REVIEWS_${getIdForIbjects(review.talent)}`],
            }),
        );
    };
    const handleDislikeReview = (review: ITalentReview) => {
        dispatch(
            dislikeTalentReview({
                reviewId: review._id,
                type: 'dislike',
                cache: [`TALENT_REVIEWS_${getIdForIbjects(review.talent)}`],
            }),
        );
    };
    const handleDeleteLike = (likeId: string, review: ITalentReview) => {
        dispatch(
            deleteLike({
                documentId: likeId,
                morePayload: {
                    like_category: 'talentReview',
                    like_type: review.user_has_liked ? 'like' : 'dislike',
                    reviewId: review._id,
                },
            }),
        );
    };

    const handleOnMessage = () => {
        setShowMessage(!showMessage);
    };

    return (
        <>
            <Container disableGutters maxWidth={false}>
                {selectedWorkProfile && (
                    <WorkProfileHeader
                        profile={selectedWorkProfile}
                        editMode={editMode}
                        onImageChange={(url, file) =>
                            handleProfileUpdate({
                                uploads: {
                                    feature_picture: [file],
                                    temp_feature_picture: url,
                                },
                                body: undefined,
                            })
                        }
                    />
                )}

                <Stack direction={'row-reverse'} justifyContent={'space-between'} alignItems={'center'}>
                    {!isPreviewMode && workProfile ? (
                        <WorkProfileSelector
                            profiles={workProfile}
                            onProfileSelect={handleOnProfileSelect}
                            onEditClick={() => setEditMode(!editMode)}
                            onCancelClick={() => setEditMode(!editMode)}
                            external={external}
                            isEditMode={editMode}
                            onSaveClick={handleUpdate}
                        />
                    ) : (
                        <PreviewModeActions
                            onAcceptClick={onAcceptApplication}
                            onRejectClick={onRejectApplication}
                            onMessageClick={onMessageApplication}
                            searchFlow={searchFlow}
                        />
                    )}

                    {isPublicMode && external && (
                        <PreviewModeActions
                            onMessageClick={handleOnMessage}
                            searchFlow={searchFlow}
                            isPublicMode={isPublicMode}
                        />
                    )}
                </Stack>

                <CustomSegmentedControl
                    tabs={[...(isSummary ? SUMMARY_TAB : DETAILS_TAB)]}
                    innerContainerStyle={{
                        width: 'fit-content',
                    }}
                    innerContainerSx={{
                        gap: 0,
                    }}
                    containerStyle={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: '10px',
                    }}
                    sx={{
                        position: 'static',
                        paddingLeft: 0,
                        paddingRight: 0,
                        top: '50px',
                        justifyContent: 'center',
                    }}
                    disregardParam
                    onHandleClick={(tab, index) =>
                        startTransition(() => {
                            setSlideName(tab);
                        })
                    }
                />
                {slideName === 'Details' && selectedWorkProfile && (
                    <CastingProfile
                        workProfile={selectedWorkProfile}
                        isEditMode={editMode}
                        onChange={handleProfileUpdate}
                        isPreviewMode={isPreviewMode}
                        applicationData={applicationData}
                    />
                )}
                {slideName === 'Media' && <Media workProfile={selectedWorkProfile} />}
                {slideName === 'Reviews' && (
                    <>
                        <Reviews
                            reviews={reviews}
                            profileUserId={selectedWorkProfile?._id}
                            onAddReview={() => setReviewModalOpen(true)}
                            onEditReview={handleEditReview}
                            onDeleteReview={handleDeleteReview}
                            onLikeReview={handleLikeReview}
                            onDislikeReview={handleDislikeReview}
                            onDeleteLikeReview={handleDeleteLike}
                        />
                        <ReviewModal
                            open={reviewModalOpen}
                            onClose={() => {
                                setReviewModalOpen(false);
                                setEditingReview(null);
                            }}
                            onSubmit={editingReview ? handleEditReviewSubmit : handleLeaveReview}
                            talentId={selectedWorkProfile?._id!}
                            initialRating={editingReview ? editingReview.rating : undefined}
                            initialSubject={editingReview ? editingReview.review_subject : undefined}
                            initialText={editingReview ? editingReview.review_text : undefined}
                        />
                        <DialogueSheet
                            open={!!deleteReview}
                            onClose={handleCancelDelete}
                            onDisagreeClick={handleCancelDelete}
                            onAgreeClicked={handleConfirmDelete}
                            header={'Delete Review'}
                            body={
                                'Warning: This action is irreversible!\nAre you sure you want to delete this review? This cannot be undone.'
                            }
                            negativeText={'Cancel'}
                            positiveText={'Delete'}
                            negtiveTextStyle={{ color: undefined }}
                            positiveTextStyle={{ color: '#ff1744' }}
                        />
                    </>
                )}
                {slideName === 'Credits' && selectedWorkProfile && (
                    <CreditsTimeline themeStyle={themeStyle} canEdit={!external} workProfile={selectedWorkProfile!} />
                )}
                {/* Message Dialogue */}
                {selectedWorkProfile && (
                    <MessageDialogue
                        open={showMessage}
                        onClose={handleOnMessage}
                        recipient={selectedWorkProfile.created_by}
                    />
                )}
            </Container>
        </>
    );
};

export { WorkProfileWrapper };
